<template>
  <div class="page-header">
    <!-- Left Section - Welcome & Info -->
    <div class="header-left">
      <div class="welcome-section">
        <!-- Greeting -->
        <h1 class="greeting-text">
          {{ greetingMessage }}
        </h1>

        <!-- Subheading -->
        <p class="welcome-message">
          {{ welcomeMessage }}
        </p>
      </div>

      <!-- Meta Info -->
      <div class="meta-info" v-if="showDate || showRefreshBtn">
        <!-- Date Badge -->
        <div class="info-badge date-badge" v-if="showDate">
          <Clock class="badge-icon" :size="12" />
          <span class="badge-text">{{ formattedDate }}</span>
        </div>

        <!-- Refresh Button -->
        <button v-if="showRefreshBtn"
          class="info-badge refresh-btn"
          @click="handleRefresh"
          :disabled="isRefreshing"
          :aria-label="isRefreshing ? $t('Webportal.Refreshing') + '...' : $t('Webportal.Refresh')">
          <RefreshCw
            class="badge-icon"
            :class="{ 'spinning': isRefreshing }"
            :size="15" />
          <span class="badge-text">
            {{ isRefreshing ? $t('Webportal.Refreshing') + '...' : $t('Webportal.Refresh') }}
          </span>
        </button>
      </div>
    </div>

    <!-- Right Section - Hero Image -->
    <div class="header-right" v-if="showHeroImage">
      <div class="hero-image-container">
        <img
          :src="heroImageUrl"
          :alt="heroImageAlt"
          class="hero-image"
          loading="lazy"
        />
        <div class="image-overlay"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Clock, RefreshCw } from 'lucide-vue-next';
import PageLayout from '@/componentsv3/PageLayout.vue';
import PageSidebar from '@/componentsv3/PageSidebar.vue';
import SurveysService from '@/services/SurveysService';
import heroImage from '@/assets/img-APP_TARGET/overview.jpg';
import heroImageFallback from '@/assets/img/overview.jpg';

export default {
  name: 'PageHeader',

  components: {
    Clock,
    RefreshCw,
    PageLayout,
    PageSidebar
  },

  props: {
    userName: {
      type: String,
      default: 'Given-Name Surname'
    },
    welcomeMessage: {
      type: String,
      default: 'Willkommen zurück im Loop Dashboard'
    },
    heroImageUrl: {
      type: String,
      default: heroImage || heroImageFallback
    },
    heroImageAlt: {
      type: String,
      default: 'Showroom / Car Lot'
    },
    showDate: {
      type: Boolean,
      default: true
    },
    showHeroImage: {
      type: Boolean,
      default: true
    },
    showRefreshBtn: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      currentDate: new Date(),
      isRefreshing: false,
      isBusy: false,
      // kpis: [],
      // structureItems: [],
      // clientId: null,
      // clientDetails: null,
      // metrics: null,
      // error: null
    }
  },

  computed: {
    ...mapGetters('localization', [ 'currentLocale' ]),

    greetingMessage() {
      const hour = new Date().getHours();
      let greeting = 'Reporting.Greeting.Day';
      if (hour < 12) {
        greeting = 'Reporting.Greeting.Morning';
      } else if (hour >= 18) {
        greeting = 'Reporting.Greeting.Evening';
      }

      return this.$t(greeting) + `, ${this.userName}`;
    },

    formattedDate() {
      const locale = this.currentLocale === 'deflt' ? 'en-GB'  : this.currentLocale;
      return this.$dayjs(this.currentDate).locale(locale || 'en').format('dddd, D. MMMM YYYY');
    },
  },

  methods: {
    handleRefresh() {
      if (this.isRefreshing) return;

      this.isRefreshing = true;

      // Emit refresh event to parent
      this.$emit('refresh');

      // Simuliere Refresh (entfernen wenn echte API-Calls vorhanden)
      setTimeout(() => {
        this.isRefreshing = false;
        this.currentDate = new Date();
      }, 1500);
    },
  },

  mounted() {
    // Update date every minute
    this.dateInterval = setInterval(() => {
      this.currentDate = new Date();
    }, 60000);
  },

  beforeUnmount() {
    if (this.dateInterval) {
      clearInterval(this.dateInterval);
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  // padding: 24px 32px 32px;

  @media (max-width: 991px) {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 400px;
  flex-shrink: 0;

  @media (max-width: 1279px) {
    width: 350px;
  }

  @media (max-width: 991px) {
    width: 100%;
  }
}

.welcome-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.greeting-text {
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.31px;
  // color: var(--bmw-primary);
  margin: 0;
  white-space: nowrap;


  @media (max-width: 991px) {
    font-size: 18px;
    white-space: normal;
  }
}

.welcome-message {
  font-size: 14.25px;
  line-height: 21px;
  letter-spacing: -0.17px;
  color: #4a5565;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 14px;
  }
}

.meta-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  @media (max-width: 639px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.info-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: var(--radius-xs);
  transition: all 0.15s ease;

  .badge-icon {
    flex-shrink: 0;
    color: currentColor;
  }

  .badge-text {
    font-size: 11.25px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: 0.05px;
    white-space: nowrap;
    color: inherit;
  }
}

.date-badge {
  color: var(--color-foreground);
  cursor: default;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.header-right {
  flex: 1;
  min-width: 0;
  height: 150px;

  @media (max-width: 991px) {
    width: 100%;
    height: 200px;
  }
}

.hero-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.hero-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: var(--radius-md);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(2, 54, 112, 0.05) 0%,
    rgba(2, 54, 112, 0.02) 100%
  );
  pointer-events: none;
}
:root.dark, [data-bs-theme="dark"] {
  .welcome-message {
    color: var(--color-muted-foreground);
  }

  .info-badge {
    background-color: var(--color-card);
    border-color: var(--color-border);
    color: var(--color-foreground);
  }

  .date-badge {
    background-color: var(--color-muted);
  }

  .hero-image-container {
  }

  .image-overlay {
    background: linear-gradient(
      135deg,
      rgba(2, 54, 112, 0.15) 0%,
      rgba(2, 54, 112, 0.05) 100%
    );
  }
}


// ============================================
// Refresh Button Styles
// ============================================
:root, [data-bs-theme=light] {
  .refresh-btn {
    height: 30px;
    color: var(--color-foreground);
    cursor: pointer;

    &:hover:not(:disabled) {
      background-color: #f9fafb;
      border-color: #d1d5db;
      // transform: translateY(-1px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    .badge-icon {
      transition: transform 0.6s ease;

      &.spinning {
        animation: spin 1s linear infinite;
      }
    }
  }

  // Accessibility
  .refresh-btn:focus-visible {
    outline: 2px solid var(--bmw-primary);
    outline-offset: 2px;
  }
}
:root.dark, [data-bs-theme="dark"] {
  .refresh-btn {
    &:hover:not(:disabled) {
      background-color: var(--color-muted);
      border-color: var(--color-border);
    }
  }
}

</style>
